"use client"
import Tooltip from '@/ui/Tooltip'
import { PERSPECTIVE_TAB_TOOLTIPS } from '@/app/common/blocks/perspectiveTabContext'
import { PLAYBOOK_BRANCH_FLOWCHART_ID, scrollToChoicePointHeader } from './choicePointAnchor'
import { EPILOGUE_TOOLTIP } from './plan-a-timeline-diagram/planATimelineDiagramModel'
import { type TimelineEndpointEntry } from './plan-a-timeline-diagram/planATimelineDiagramModel'
import { closeSelectedEnding, escapePerspectiveIfNeeded } from './plan-a-timeline-diagram/timelineDiagramNavigation'
import { type SelectedEnding } from './plan-a-timeline-diagram/useSelectedEnding'
import { useStandaloneDiagramProps } from './plan-a-timeline-diagram/useStandaloneDiagramProps'
import { useTimelineDiagramNavigation } from './plan-a-timeline-diagram/useTimelineDiagramNavigation'

// The end-of-scenario nav hub rendered by ScenarioEndFooter: a 2×2 grid of
// the four places a reader can go next. Same underlying navigation as the
// timeline diagram it replaced — ending buttons expand the matching section
// in place via the URL hash (or escape a standalone `?perspective=` page),
// and "Choose another Plan" scrolls back up to the plan-choice flowchart.

// One cell of the grid. The button's own label doubles as the tooltip header,
// matching the timeline diagram's label tooltips (see DiagramLabel).
// Below md (where the timeline diagram doesn't render) the cells speak the
// scenario's CTA language — the "Read Plan A" button and the flowchart's
// PLAN A card: bold tracking-wide label in the inherited et-book face (no
// font-sans), centered in a rounded-md full-black border, centered serif
// subtitle, black fill while its ending is open. From md up they keep the
// roomier left-aligned serif-card look under the timeline diagram.
const EndNavButton = ({
  active = false,
  onClick,
  subtitle,
  children,
}: {
  active?: boolean
  onClick: () => void
  subtitle: string
  children: React.ReactNode
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-md border-2 border-black px-3 py-5 gap-2 text-center items-center justify-center md:border-[1.5px] md:p-6 md:gap-4 md:text-left md:items-stretch md:justify-between flex flex-col transition-colors cursor-pointer ${
      active ? 'bg-black text-[#fffff8]' : 'hover:bg-black/5 md:border-black/30'
    }`}
  >
    <div className="text-xl leading-tight font-bold tracking-wide md:text-3xl md:font-serif md:font-medium md:tracking-normal md:leading-normal">{children}</div>
    <div className={`text-sm leading-snug tracking-wide font-serif text-balance md:text-base md:leading-normal md:tracking-normal ${active ? 'text-[#fffff8]/80' : 'text-black/90 md:text-black/60'}`}>
      {subtitle}
    </div>
  </button>
)

export const ScenarioEndNavGrid = ({
  // Forwarded to the hash wiring (see useStandaloneDiagramProps). The grid's
  // own ending buttons route purely through the hash now, but the wiring also
  // registers the router-aware escape used on `?perspective=` pages.
  inlinePlanAContinuation = false,
}: { inlinePlanAContinuation?: boolean } = {}) => {
  const diagramProps = useStandaloneDiagramProps(inlinePlanAContinuation)
  const { handleEndpoint, selected, selectedEnding } = useTimelineDiagramNavigation(diagramProps)

  // Jump back to the top-of-page plan-choice flowchart. On a `?perspective=`
  // page the playbook (and the flowchart) isn't mounted, so escape to the
  // main page with the flowchart id as the hash — useAnchorDeepLink's DOM-id
  // fallback scrolls to it after load.
  // Re-clicking the ending that's already open closes it (animates back to
  // height 0) instead of re-navigating. Only applies to in-place expansion
  // (`selectedEnding`, hash-driven) — on a standalone `?perspective=` page the
  // page IS the ending, so clicking its own button stays a no-op navigation.
  const toggleEndpoint = (ending: SelectedEnding, entry: TimelineEndpointEntry) => {
    if (selectedEnding === ending) {
      closeSelectedEnding()
      return
    }
    handleEndpoint(entry)
  }

  const chooseAnotherPlan = () => {
    if (escapePerspectiveIfNeeded(PLAYBOOK_BRANCH_FLOWCHART_ID)) return
    scrollToChoicePointHeader()
  }

  // An ending highlights while open: via the live hash (`selectedEnding`,
  // in-place expansion on the main page) or the page-level `?perspective=`
  // mode (`selected`, the only signal on a standalone POV page).
  // The 10px mobile gutter matches the Choose-a-Path plan row (see
  // MobilePlaybookBranchChoicesFive); md+ keeps the old gap.
  return (
      <div className="grid grid-cols-2 gap-[10px] md:gap-3 mb-8">
        <EndNavButton
          active={selectedEnding === 'public' || selected === 'public'}
          subtitle="What Plan A feels like to an average citizen"
          onClick={() => toggleEndpoint('public', { kind: 'perspective', label: 'Public POV', perspective: 'public' })}
        >
          Public Perspective
        </EndNavButton>
        <EndNavButton
          active={selectedEnding === 'insider' || selected === 'insider'}
          subtitle="What Plan A feels like to an AI researcher"
          onClick={() => toggleEndpoint('insider', { kind: 'perspective', label: 'Insider POV', perspective: 'insider' })}
        >
          Insider Perspective
        </EndNavButton>
        <EndNavButton
          active={selectedEnding === 'epilogue'}
          subtitle="Life after superintelligence"
          onClick={() => toggleEndpoint('epilogue', { kind: 'epilogue', label: 'Epilogue' })}
        >
          Epilogue
        </EndNavButton>
        <EndNavButton
          subtitle="Read another plan scenario"
          onClick={chooseAnotherPlan}
        >
          Choose another path
        </EndNavButton>
      </div>
  )
}
